<template>
  <div>
    <Button
      v-if="showButton"
      label="Send SMS"
      size="small"
      @click="toggleOpen"
    >
      <template #icon>
        <Icon name="material-symbols:chat-bubble" />
      </template>
    </Button>

    <Dialog
      v-model:visible="visible"
      header="Send SMS"
      :style="{ width: '600px' }"
      :modal="true"
    >
      <div
        v-if="visible"
        class="grid grid-cols-1 gap-y-5"
      >
        <p class="text-sm text-gray-500">
          Notify this contact via SMS. Choose a template — the message content is fixed.
        </p>

        <div class="grid grid-cols-1">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            To
          </label>
          <InputText
            v-model="payload.recipient"
            placeholder="Enter phone number"
            class="w-full"
          />
        </div>

        <MessageTemplateSelector
          v-model:selected-template-id="selectedTemplateId"
          placeholder="Select a template"
          @select="onTemplateSelect"
        />

        <div class="relative">
          <label class="block text-sm font-medium text-gray-700 mb-2">
            Message
          </label>
          <div class="relative">
            <div
              class="w-full min-h-[6rem] rounded-md border border-gray-300 bg-gray-50 px-3 py-2 text-sm whitespace-pre-wrap break-words"
              :class="payload.body ? 'text-gray-700' : 'text-gray-400'"
            >
              {{ payload.body || 'Select a template to preview the message' }}
            </div>
            <div
              v-if="isResolving"
              class="absolute inset-0 flex items-center justify-center bg-white/70 rounded-md"
            >
              <i class="pi pi-spin pi-spinner text-xl text-gray-500" />
            </div>
          </div>
          <div class="flex justify-end mt-1">
            <span
              class="text-xs"
              :class="charCountColor"
            >
              {{ payload.body?.length || 0 }} / 160
            </span>
          </div>
        </div>
        <div class="flex justify-end">
          <Button
            label="Send"
            class="mt-4"
            :loading="isLoading"
            :disabled="!payload.recipient || !payload.body?.trim()"
            @click="sendSMS"
          />
        </div>
      </div>
    </Dialog>
  </div>
</template>

<script setup lang="ts">
import type { MessageTemplate } from '@prisma/client'

const visible = ref(false)

const props = withDefaults(defineProps<{
  recipient: string
  contactId: string
  businessId?: string
}>(), {
  businessId: '',
})

const showButton = defineModel<boolean>('showButton', {
  required: false,
  default: true,
})
const toggleOpen = () => {
  visible.value = !visible.value
}

const payload = ref({
  recipient: '',
  subject: '',
  body: '',
})

const selectedTemplateId = ref<string | null>(null)
const isResolving = ref(false)

const charCountColor = computed(() => {
  const len = payload.value.body?.length || 0
  if (len > 320) return 'text-red-500 font-semibold'
  if (len > 160) return 'text-yellow-500'
  return 'text-gray-500'
})

const { resolve: resolveTemplate } = useMessageTemplate()

let resolveSeq = 0
const onTemplateSelect = async (template: MessageTemplate | null) => {
  if (!template) {
    payload.value.body = ''
    return
  }
  const seq = ++resolveSeq
  isResolving.value = true
  const { data } = await resolveTemplate({
    templateId: template.id,
    contactId: props.contactId,
    businessId: props.businessId,
  })
  // Ignore stale responses if a newer template was selected meanwhile
  if (seq !== resolveSeq) {
    return
  }
  payload.value.body = data?.body ?? template.body
  isResolving.value = false
}

defineExpose({
  toggleOpen,
})

const { sendSMS: sendSmsApi } = useUtils()
const toast = useToast()
const emit = defineEmits(['refresh'])

const isLoading = ref(false)
const sendSMS = async () => {
  isLoading.value = true

  const { error } = await sendSmsApi({
    to: payload.value.recipient,
    body: payload.value.body,
    contactId: props.contactId,
    businessId: props.businessId,
  })

  if (error) {
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to send SMS', life: 3000 })
  }
  else {
    toast.add({ severity: 'success', summary: 'Success', detail: 'SMS sent successfully', life: 3000 })
    emit('refresh')
    visible.value = false
  }

  isLoading.value = false
}

onMounted(() => {
  payload.value.recipient = props.recipient
})
</script>
